import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useResponsive } from '../../../hooks/use-responsive';
import {AuroraText} from '../../ui/aurora-text';
import { ExternalLink, Settings } from 'lucide-react';

// iOS风格的弹簧动画配置
const iosSpringConfig = {
  type: "spring",
  stiffness: 300,
  damping: 25,
  mass: 0.8
};

export const ServiceStatusCard = ({ selectedService, handleServiceSwitch }) => {
  const { isMobile } = useResponsive();
  
  // 服务名称处理
  const serviceName = selectedService.name?.replace('Key查询', '') || '全模型';
  
  return {
    className: "col-span-1 row-span-1 group relative overflow-hidden",
    onClick: handleServiceSwitch,
    background: (
      <div className="absolute inset-0 bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-600"> 
        {/* 装饰图标 - 移动端简化 */}
        {isMobile ? (
          <motion.div 
            className="absolute right-3 top-3 opacity-20"
            animate={{ 
              rotate: [0, 10, -10, 0],
              scale: [1, 1.1, 1]
            }}
            transition={{
              duration: 3,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          >
            <Settings className="h-8 w-8 text-gray-600 dark:text-gray-400" />
          </motion.div>
        ) : (
          <motion.div 
            className="absolute right-6 bottom-6 opacity-30 transition-all duration-300 ease-out group-hover:opacity-50 group-hover:scale-110"
            animate={{
              y: [0, -5, 0],
              rotate: [0, 2, -2, 0]
            }}
            transition={{
              duration: 4,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          >
            <div className="w-16 h-16 bg-black dark:bg-white rounded-full" />
            <div className="w-12 h-12 bg-black dark:bg-white rounded-full absolute -top-8 -left-4 opacity-60" />
            <div className="w-8 h-8 bg-black dark:bg-white rounded-full absolute -top-12 left-2 opacity-40" />
          </motion.div>
        )}

        {/* 点击时的涟漪效果 */}
        <motion.div
          className="absolute inset-0 bg-blue-500/10 rounded-2xl opacity-0 group-active:opacity-100"
          initial={false}
          transition={{ duration: 0.2, ease: "easeOut" }}
        />
      </div>
    ),
    children: (
      <motion.div 
        className={`relative z-20 h-full flex flex-col justify-between ${isMobile ? 'p-4' : 'p-6'}`}
        whileHover={{
          scale: 1.02,
          transition: { duration: 0.2, ease: "easeOut" }
        }}
        whileTap={{
          scale: 0.98,
          transition: { duration: 0.1, ease: "easeInOut" }
        }}
      >
        {isMobile ? (
          // 移动端引导式布局
          <>
            {/* 顶部标题 */}
            <div className="flex-1 flex flex-col justify-center">
              <motion.div 
                className="text-gray-500 dark:text-gray-400 text-xs font-medium tracking-wider uppercase mb-2"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={iosSpringConfig}
              >
                当前服务
              </motion.div>
              
              {/* 服务名称 - 动态切换效果 - 移动端更大更粗 */}
              <AnimatePresence mode="wait">
                <motion.div
                  key={serviceName}
                  initial={{ opacity: 0, x: 20, scale: 0.95 }}
                  animate={{ 
                    opacity: 1, 
                    x: 0, 
                    scale: 1,
                    transition: {
                      ...iosSpringConfig,
                      duration: 0.6
                    }
                  }}
                  exit={{ 
                    opacity: 0, 
                    x: -20, 
                    scale: 0.95,
                    transition: { duration: 0.3 }
                  }}
                >
                  <AuroraText className="text-xl sm:text-2xl md:text-xl font-black text-gray-800 dark:text-gray-200 leading-tight">
                    {serviceName}
                  </AuroraText>
                </motion.div>
              </AnimatePresence>
            </div>
            
            {/* 底部切换提示 */}
            <motion.div 
              className="flex items-center justify-center gap-1 text-xs text-gray-500 dark:text-gray-400"
              animate={{
                y: [0, -2, 0]
              }}
              transition={{
                duration: 2,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            >
              <motion.div
                animate={{ rotate: [0, 15, -15, 0] }}
                transition={{
                  duration: 3,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
              >
                <ExternalLink className="h-3 w-3" />
              </motion.div>
              <span>切换</span>
            </motion.div>
          </>
        ) : (
          // 桌面端原有布局
          <>
            <motion.div 
              className="text-gray-500 dark:text-gray-400 text-xs font-medium tracking-wider uppercase"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={iosSpringConfig}
            >
              SERVICE STATUS
            </motion.div>
            
            <div className="flex-1 flex items-center">
              <div>
                <motion.h3 
                  className="text-gray-800 dark:text-gray-200 text-xl font-bold leading-tight mb-2"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ ...iosSpringConfig, delay: 0.1 }}
                >
                  当前服务
                </motion.h3>
                
                {/* 服务名称 - 动态切换效果 - 桌面端更大更粗 */}
                <AnimatePresence mode="wait">
                  <motion.div
                    key={serviceName}
                    initial={{ opacity: 0, x: 30, scale: 0.9 }}
                    animate={{ 
                      opacity: 1, 
                      x: 0, 
                      scale: 1,
                      transition: {
                        ...iosSpringConfig,
                        duration: 0.8
                      }
                    }}
                    exit={{ 
                      opacity: 0, 
                      x: -30, 
                      scale: 0.9,
                      transition: { duration: 0.4 }
                    }}
                  >
                    <AuroraText className="text-2xl lg:text-3xl xl:text-4xl font-black leading-tight">
                      {serviceName}
                    </AuroraText>
                  </motion.div>
                </AnimatePresence>
              </div>
            </div>
            
            <motion.div 
              className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 group-hover:text-gray-600 dark:group-hover:text-blue-400 transition-colors"
              animate={{
                x: [0, 5, 0]
              }}
              transition={{
                duration: 2.5,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            >
              <motion.div
                animate={{ rotate: [0, 15, -15, 0] }}
                transition={{
                  duration: 3,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
              >
                <ExternalLink className="h-4 w-4" />
              </motion.div>
              点击切换
            </motion.div>
          </>
        )}
      </motion.div>
    ),
  };
}; 